import * as React from 'react';

import TranslationDisplay from './TranslationDisplay';

export interface IStaffReplyTranslation {
  sourceBody: string;
  translatedBody: string;
  translationLanguage: string;
  translationLabel: string | null;
}

type StaffReplyTranslationSource = Pick<
  IEmailResponse,
  'sourceBody' | 'translatedBody' | 'translationLanguage' | 'translationLabel'
> & {
  type: string;
};

function payloadLabel(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : null;
}

export function getStaffReplyTranslation(message: StaffReplyTranslationSource): IStaffReplyTranslation | null {
  const { type, sourceBody, translatedBody, translationLanguage } = message;

  if (type !== EmailType.OUTBOUND || !sourceBody || !translatedBody || !translationLanguage) {
    return null;
  }

  return {
    sourceBody,
    translatedBody,
    translationLanguage,
    translationLabel: payloadLabel(message.translationLabel),
  };
}

/**
 * Signature, footer, and quoted history that follow the staff English in a stored body. The delivered email
 * places the translation between the two, so the server stores `body` as `sourceBody` plus this remainder.
 */
export function getSentBodyRemainder(body: string, sourceBody: string): string | null {
  return body.startsWith(sourceBody) ? body.slice(sourceBody.length) : null;
}

export interface IStaffReplyTranslationProps {
  /** The staff-written English, rendered by the caller in its own message markup. */
  original: React.ReactNode;
  translatedText: string;
  languageCode: string;
  /** Resident-language label from the API. Missing values use the English label. */
  translationLabel?: string | null;
  format: 'text' | 'html';
}

export default function StaffReplyTranslation({
  original,
  translatedText,
  languageCode,
  translationLabel,
  format,
}: IStaffReplyTranslationProps): JSX.Element {
  const label = payloadLabel(translationLabel);

  return (
    <TranslationDisplay
      className="staff-reply-translation"
      originalContent={original}
      englishTranslation={translatedText}
      translationLanguageCode={languageCode}
      translationFormat={format}
      translated={true}
      aiTranslationDisclaimer={label || undefined}
      aiTranslationDisclaimerLanguage={label ? languageCode : undefined}
    />
  );
}
